/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import axios from 'axios';
import Swal from 'sweetalert2';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {
  Collapse,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Media,
  Navbar,
  NavItem,
  Nav,
  Container,
} from 'reactstrap';

import { logout, checkPassword, changePassword } from 'apis/admin/userApi';
import { deleteCookie } from 'lib/cookies';

function AdminNavbar({ theme, sidenavOpen, toggleSidenav }) {
  const user = useSelector(state => state.user);
  const history = useHistory();

  const logoutFunction = async () => {
    await logout()
      .then(() => {
        delete axios.defaults.headers.common['Authorization'];
        deleteCookie('access');
        deleteCookie('refresh');

        window.location.replace('/auth/login');
      })
      .catch((error) => {
        console.error(
          'error occured in AdminNavbar.js - handleLogout() ',
          error
        );

        delete axios.defaults.headers.common['Authorization'];
        deleteCookie('access');
        deleteCookie('refresh');

        window.location.replace('/auth/login');
      });
  };

  const handleLogout = () => {
    Swal.fire({
      title: '로그아웃 하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '로그아웃',
      cancelButtonText: '취소',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await logoutFunction();
      } else {
        Swal.close();
      }
    });
  };

  const handleChangePassword = async () => {
    Swal.fire({
      title: '비밀번호를 변경 하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '비밀번호 변경',
      cancelButtonText: '취소',
      focusConfirm: false,
    }).then(async (value) => {
      if (value.isConfirmed) {
        const { value: password } = await Swal.fire({
          title: '비밀번호를 입력하세요.',
          input: 'password',
          inputLabel: '현재 사용 중인 비밀번호를 입력하세요.',
          inputAttributes: {
            autocapitalize: 'off',
            autocorrect: 'off',
          },
          showCancelButton: true,
          confirmButtonText: '확인',
          cancelButtonText: '취소',
          focusConfirm: false,
        });

        if (password) {
          await checkPassword({ userid: user && user.userData.userid, password })
            .then(async (response) => {
              if (response.data.success) {                
                // 새 비밀번호 입력
                const { value: pwValues } = await Swal.fire({
                  title: '새 비밀번호를 입력하세요.',
                  html:
                    '<input type="password" id="password1" placeholder="새 비밀번호" class="swal2-input">' +
                    '<input type="password" id="password2" placeholder="새 비밀번호 확인" class="swal2-input">',
                  focusConfirm: false,
                  preConfirm: () => {
                    return [
                      document.getElementById('password1').value,
                      document.getElementById('password2').value
                    ]
                  }
                });

                if (pwValues[0] === pwValues[1]) {
                  await changePassword({ userid: user && user.userData.userid, password: pwValues[0]})
                    .then((response) => {
                      if (response.data.success) {
                        Swal.fire({
                          icon: 'success',
                          title: '비밀번호를 변경하였습니다.',
                          text: '다시 로그인 해주세요.',
                        }).then(async () => {
                          await logoutFunction();
                        });
                      }
                    })
                    .catch(() => {
                      Swal.fire({
                        icon: 'error',
                        title: '사용자가 존재하지 않습니다.'
                      })
                      .then(() => {
                        Swal.close();
                      });
                    });
                } else {
                  Swal.fire({
                    icon: 'error',
                    title: '비밀번호가 서로 일치하지 않습니다.'
                  })
                  .then(() => {
                    return false;
                  });
                }

              } else {
                Swal.fire({
                  title: '비밀번호가 맞지 않습니다.',
                  icon: 'error',
                });
              }
            })
            .catch((error) => {
              console.error('error occured in Member.js - handleChangePassword() ', error);

              Swal.fire({
                title: '비밀번호가 다르거나 사용자가 없습니다.',
                icon: 'warning',
              });
            });
        }
      } else {
        Swal.close();
      }
    });
  };

  return (
    <>
      <Navbar
        className={classnames(
          'navbar-top navbar-expand',
          { 'navbar-dark bg-gradient-purple': theme === 'dark' },
          { 'navbar-light bg-secondary': theme === 'light' }
        )}
      >
        <Container fluid>
          <Collapse navbar isOpen={true}>
            <Nav className="align-items-center ml-md-auto" navbar>
              <NavItem className="d-xl-none">
                <div
                  className={classnames(
                    'pr-3 sidenav-toggler',
                    { active: sidenavOpen },
                    { 'sidenav-toggler-dark': theme === 'dark' }
                  )}
                  onClick={toggleSidenav}
                >
                  <div className="sidenav-toggler-inner">
                    <i className="sidenav-toggler-line" />
                    <i className="sidenav-toggler-line" />
                    <i className="sidenav-toggler-line" />
                  </div>
                </div>
              </NavItem>
            </Nav>
            <Nav className="align-items-center ml-auto ml-md-0" navbar>
              <UncontrolledDropdown nav>
                <DropdownToggle className="nav-link pr-0" color="" tag="a">
                  <Media
                    className="align-items-center"
                    style={{ cursor: 'pointer' }}
                  >
                    <span>
                      <i className="ni ni-circle-08 text-white" />
                    </span>
                    <Media className="ml-2 d-none d-lg-block">
                      <span className="mb-0 text-sm font-weight-bold text-white">
                        {user && user.userData && user.userData.username}
                      </span>
                    </Media>
                  </Media>
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem className="noti-title" header tag="div">
                    <h6 className="text-overflow m-0">안녕하세요, {user && user.userData && user.userData.username}님!</h6>
                  </DropdownItem>
                  <DropdownItem onClick={() => window.open('/', '_blank')}>
                    <i className="ni ni-compass-04" />
                    <span>홈페이지로 이동</span>
                  </DropdownItem>
                  <DropdownItem onClick={handleChangePassword}>
                    <i className="ni ni-key-25" />
                    <span>비밀번호 변경</span>
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem onClick={handleLogout}>
                    <i className="ni ni-user-run" />
                    <span>로그아웃</span>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </Nav>
          </Collapse>
        </Container>
      </Navbar>
    </>
  );
}

AdminNavbar.defaultProps = {
  toggleSidenav: () => {},
  sidenavOpen: false,
  theme: 'dark',
};
AdminNavbar.propTypes = {
  toggleSidenav: PropTypes.func,
  sidenavOpen: PropTypes.bool,
  theme: PropTypes.oneOf(['dark', 'light']),
};

export default AdminNavbar;
